import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { lighten, darken, alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/GetApp";
import { printImage } from "./printFunctions";
import { SubjectsList, GradeLevels, ImageTagsList } from "./BankFilters";

import Logger from "./Logger";
import EditIcon from "@mui/icons-material/Edit";
import ImageEditDialog from "./ImageEditDialog";

import CopyIcon from "@mui/icons-material/FileCopy";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ImageDialog from "./ImageDialog";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  startAfter,
  limit,
} from "firebase/firestore";

import { db } from "./firebase";
import getListStyles from "./listStyles";

import StarOutlineIcon from "@mui/icons-material/StarOutline";
import StarIcon from "@mui/icons-material/Star";
import { doc, updateDoc } from "firebase/firestore";

const ImageBank = ({ open, onClose, user, styles, theme }) => {
  const [images, setImages] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedQuizTypes, setSelectedQuizTypes] = useState([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedImageForEdit, setSelectedImageForEdit] = useState(null);

  const { listItemStyles, listItemIconStyles, listItemTextStyles } =
    getListStyles(theme, theme.palette.mode === "dark");

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadedPages] = useState(new Set());

  const [selectedTags, setSelectedTags] = useState([]);

  // Modify the useEffect that runs when dialog opens
  // useEffect(() => {
  //   if (open) {
  //     setIsInitialLoad(true);
  //     fetchImages();
  //   }
  // }, [open]);

  // Replace the existing useEffect with this:
  useEffect(() => {
    if (open) {
      // Reset everything
      setLastDoc(null);
      setHasMore(true);
      setImages([]);
      setIsInitialLoad(true);

      // Use the same pagination approach for both filtered and unfiltered
      fetchImages();
    }
  }, [open, selectedTags]);

  const toggleFeatured = async (e, image) => {
    e.stopPropagation();
    try {
      const imageRef = doc(db, "images", image.id);
      await updateDoc(imageRef, {
        featured: !image.featured,
      });

      // Update local state
      setImages(
        images.map((img) =>
          img.id === image.id ? { ...img, featured: !img.featured } : img,
        ),
      );
    } catch (error) {
      console.error("Error updating featured status:", error);
    }
  };

  const fetchImages = async () => {
    if (isLoading || (!hasMore && lastDoc)) return;
    console.log("=== FETCH IMAGES STARTING ===");
    console.log("Current state:", {
      isLoading,
      hasMore,
      lastDoc,
      selectedTags,
    });

    if (isLoading || (!hasMore && lastDoc)) {
      console.log("EARLY RETURN because:", { isLoading, hasMore, lastDoc });
      return;
    }

    console.log("--- Starting new fetch ---");
    console.log(
      "Current lastDoc timestamp:",
      lastDoc ? lastDoc.data().createdAt.toDate() : "Initial fetch",
    );

    setIsLoading(true);
    try {
      const imagesRef = collection(db, "images");

      // Start building the query
      let queryConstraints = [orderBy("createdAt", "desc")];

      // Add tag filter if tags are selected
      if (selectedTags.length > 0) {
        console.log("Selected tags:", selectedTags);
        console.log("Adding tag filter with array-contains-any");
        queryConstraints.push(
          where("tags", "array-contains-any", selectedTags),
        );
      }

      // Add pagination constraints
      if (lastDoc) {
        queryConstraints.push(startAfter(lastDoc));
      }
      queryConstraints.push(limit(IMAGES_PER_PAGE));

      // Create the query with all constraints
      const q = query(imagesRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);

      console.log(
        "Query results:",
        querySnapshot.docs.map((doc) => ({
          id: doc.id,
          tags: doc.data().tags,
        })),
      );

      console.log("Fetched docs timestamps:");
      querySnapshot.docs.forEach((doc, i) => {
        console.log(
          `[${i}] ID: ${doc.id}, Date: ${doc.data().createdAt.toDate()}`,
        );
      });

      const fetchedImages = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      console.log(
        "New lastDoc timestamp:",
        newLastDoc ? newLastDoc.data().createdAt.toDate() : "No new docs",
      );

      setLastDoc(newLastDoc);
      setHasMore(querySnapshot.docs.length === IMAGES_PER_PAGE);

      console.log("Current images count:", images.length);
      setImages((prevImages) => {
        const newImages = [...prevImages, ...fetchedImages];
        console.log("New total images count:", newImages.length);
        console.log("Last 5 dates in state:");
        newImages.slice(-5).forEach((img, i) => {
          console.log(
            `[${newImages.length - 5 + i}] ID: ${img.id}, Date: ${img.createdAt.toDate()}`,
          );
        });
        return newImages;
      });
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  };

  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const IMAGES_PER_PAGE = 10;

  // Add this function to detect when user is near bottom of scroll
  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 100;
    if (bottom && !isLoading && hasMore) {
      fetchImages();
    }
  };

  // const filteredImages = images.filter((image) => {
  //   if (selectedTags.length === 0) {
  //     return true; // Show all images when no tags are selected
  //   }

  //   // Check if image has tags array
  //   if (!image.tags || !Array.isArray(image.tags)) {
  //     return false;
  //   }

  //   // Show image if it has ANY of the selected tags (OR condition)
  //   return selectedTags.some((tag) => image.tags.includes(tag));
  // });

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
  };

  const downloadImage = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "25px",
            height: "90vh",
            bgcolor: theme.palette.mode === "dark" ? "#192734" : "#fff",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "700", fontSize: "1.5rem" }}
            >
              Bildbanken
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }} onScroll={handleScroll}>
          <Grid container spacing={2}>
            {/* Left Sidebar - Subject Filter */}
            <Grid item xs={12} md={3}>
              <ImageTagsList
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                theme={theme}
              />
            </Grid>
            {/* Right Content - Images */}
            <Grid item xs={12} md={9}>
              {/* Images Grid */}
              <Grid container spacing={2}>
                {isInitialLoad ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <CircularProgress size={50} />
                    <Typography sx={{ mt: 2, color: "text.secondary" }}>
                      Laddar bilder...
                    </Typography>
                  </Box>
                ) : images.length === 0 ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <img
                      src="/img/empty.png"
                      alt="Empty"
                      style={{ width: "200px", marginBottom: "24px" }}
                    />
                    <Typography
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        fontWeight: "500",
                        fontSize: "1.3rem",
                        width: { xs: "100%", md: "70%" },
                      }}
                    >
                      Här var det tomt! Vi jobbar på att fylla på Bildbanken.
                      Titta in snart igen! :)
                    </Typography>
                    <Button
                      variant="print"
                      onClick={() => {
                        setSelectedTags([]);
                      }}
                      sx={{ mt: 4 }}
                    >
                      Rensa filter
                    </Button>
                  </Box>
                ) : (
                  images.map((image) => (
                    <>
                      <Grid item xs={12} sm={6} md={4} key={image.id}>
                        <Box
                          sx={{
                            ...styles.freshBox,
                            borderRadius: "18px",
                            padding: 0,
                            height: "250px",
                            position: "relative",
                            overflow: "hidden",
                            cursor: "pointer",
                            "&:hover": {
                              "& .hover-actions": {
                                opacity: 1,
                              },
                            },
                          }}
                          onClick={() => handleImageClick(image)}
                        >
                          <img
                            src={image.imageUrl}
                            alt={image.prompt}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />

                          {/* Hover Actions */}
                          <Box
                            className="hover-actions"
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0, 0, 0, 0.5)",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              opacity: 0,
                              transition: "opacity 0.3s",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "white",
                                textAlign: "center",
                                px: 2,
                                mb: 2,
                                fontWeight: "500",
                                display: "none",
                              }}
                            >
                              {image.prompt}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              {user.admin && (
                                <>
                                  <Tooltip
                                    title={
                                      image.featured
                                        ? "Ta bort från veckans paket"
                                        : "Lägg till i veckans paket"
                                    }
                                  >
                                    <IconButton
                                      onClick={(e) => toggleFeatured(e, image)}
                                      sx={{ color: "white" }}
                                    >
                                      {image.featured ? (
                                        <StarIcon />
                                      ) : (
                                        <StarOutlineIcon />
                                      )}
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Tagga">
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedImageForEdit(image);
                                        setEditDialogOpen(true);
                                      }}
                                      sx={{ color: "white" }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip title="Ladda ned">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadImage(
                                      image.imageUrl,
                                      `studera-ai-${image.id}.png`,
                                    );
                                  }}
                                  sx={{ color: "white" }}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Skriv ut">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    printImage(image.imageUrl); // Pass your image URL here
                                  }}
                                  sx={{
                                    color: "white",
                                  }}
                                >
                                  <PrintIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Kopiera prompt">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(image.prompt);
                                  }}
                                  sx={{ color: "white" }}
                                >
                                  <CopyIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          {/* Bottom Info Bar */}
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              p: 1.5,
                              background:
                                "linear-gradient(transparent, rgba(0,0,0,1))",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <ThumbUpOffAltIcon
                                sx={{ fontSize: "17px", color: "white" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "15px",
                                  lineHeight: 1,
                                  fontWeight: "600",
                                  color: "white",
                                }}
                              >
                                {image.upvotes || 0}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "white" }}
                            >
                              {new Date(
                                image.createdAt?.toDate(),
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            flex: 1,
                            mt: 0.5,
                          }}
                        >
                          {image.tags?.map((tag, index) => (
                            <Chip key={index} label={tag} size="small" />
                          ))}
                        </Box>
                      </Grid>
                    </>
                  ))
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      {selectedImageForEdit && (
        <ImageEditDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedImageForEdit(null);
          }}
          image={selectedImageForEdit}
          theme={theme}
          styles={styles}
        />
      )}
      {/* Image Dialog */}
      {selectedImage && (
        <ImageDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          selectedImage={selectedImage}
          onDownload={() => {
            if (selectedImage) {
              downloadImage(
                selectedImage.imageUrl,
                `studera-ai-${selectedImage.id}.png`,
              );
            }
          }}
          onCopyPrompt={() => {
            if (selectedImage) {
              navigator.clipboard.writeText(selectedImage.prompt);
            }
          }}
        />
      )}
    </>
  );
};

export default ImageBank;
